/*
 * ============================================================
 * CCS SELLER API CLIENT — assets/ccs-api.js
 * ============================================================
 * Thin wrapper over the App Proxy at /apps/ccs.
 *
 * Every call is same-origin, so the browser sends the session cookie and
 * Shopify signs the request with the shopper's identity before it reaches the
 * Lambda. That means there is no token to hold here and nothing to authenticate
 * with -- a request either comes from a signed-in seller or it does not, and
 * this file cannot influence which.
 *
 * Consequently: never send a store handle, seller email or customer id to say
 * WHO is acting. The server ignores it. The only identity-ish parameter is
 * `as`, which is support impersonation and is refused unless the signed-in
 * account is an allowlisted admin.
 */
(function (window) {
  'use strict';

  var BASE = '/apps/ccs';

  /*
   * The API answers with { success, data } or { success, error }. Errors are
   * turned into rejections carrying the server's own message, because those
   * messages are written for the seller -- "That store URL is already taken."
   * reads better than anything this layer could invent from a status code.
   */
  function request(path, options) {
    var opts = options || {};
    var url = BASE + path;

    // Support sessions carry ?as= on every call. Held here rather than passed
    // at each call site so no surface can forget it and silently act as the
    // admin's own (usually empty) store.
    var actingAs = CCSApi.actingAs;
    if (actingAs) {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'as=' + encodeURIComponent(actingAs);
    }

    var init = {
      method: opts.method || 'GET',
      headers: { 'Accept': 'application/json' },
      // Same-origin, but stated explicitly: without the cookie Shopify cannot
      // tell who is signed in and every call comes back as logged out.
      credentials: 'same-origin'
    };
    if (opts.body) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }

    return fetch(url, init).then(function (response) {
      return response.json().catch(function () {
        // A non-JSON body means the request never reached the Lambda -- a
        // missing proxy, or Shopify serving its own error page.
        throw new Error('The server could not be reached. Please try again.');
      }).then(function (payload) {
        if (!response.ok || !payload || payload.success === false) {
          var error = new Error((payload && payload.error) || 'Something went wrong.');
          error.status = response.status;
          error.payload = payload;
          throw error;
        }
        // `actor` rides alongside `data` rather than inside it; hand both back.
        if (payload.actor) CCSApi.actor = payload.actor;
        return payload.data;
      });
    });
  }

  var CCSApi = {
    /*
     * Set to a store handle or seller email to act as that seller. Only has
     * any effect for an allowlisted admin; for anyone else the server refuses
     * the request outright rather than ignoring the parameter.
     */
    actingAs: null,

    // Populated from the last response that carried one: { isAdmin,
    // impersonating, target }. Null for an ordinary seller.
    actor: null,

    getStore: function () { return request('/store'); },

    createStore: function (fields) {
      return request('/store', { method: 'POST', body: fields });
    },

    updateStore: function (fields) {
      return request('/store', { method: 'PUT', body: fields });
    },

    checkHandle: function (handle) {
      return request('/store/handle-available?handle=' + encodeURIComponent(handle));
    },

    getStyle: function (handle) {
      return request('/style?handle=' + encodeURIComponent(handle));
    },

    /*
     * Price range for a markup. Drives the pricing slider, and is computed by
     * the same code that prices the listing on save -- so what the seller is
     * shown is what they get.
     */
    quote: function (payload) {
      return request('/quote', { method: 'POST', body: payload });
    },

    /*
     * One of the seller's own listings, in the form the builder can reopen:
     * the fabric scene per side, the sizes, the markup. The shopper-facing
     * view that comes back with the store deliberately drops all three.
     */
    getListing: function (uid) {
      return request('/listing?uid=' + encodeURIComponent(uid));
    },

    createListing: function (listing) {
      return request('/listing', { method: 'POST', body: listing });
    },

    // Accepts name, description, markup and defaultColorKey. Anything left
    // out is left alone -- editing a name is not a statement about color.
    updateListing: function (listing) {
      return request('/listing', { method: 'PUT', body: listing });
    },

    /*
     * Save a re-run of the builder over the listing that produced it.
     *
     * Same body as createListing plus a uid. The listing keeps that uid, so
     * the product keeps its handle, its URL and its place in the seller's
     * categories -- which is the difference between editing and duplicating.
     */
    updateListingDesign: function (listing) {
      return request('/listing/design', { method: 'PUT', body: listing });
    },

    deleteListing: function (uid) {
      return request('/listing', { method: 'DELETE', body: { uid: uid } });
    },

    /*
     * Is a blank in stock? Answered from the cached SIB style feed, so a
     * product page can call it on every size click without a storefront
     * request each time.
     */
    checkInventory: function (styleHandle, gtin) {
      return request('/inventory?handle=' + encodeURIComponent(styleHandle) +
        '&gtin=' + encodeURIComponent(gtin));
    },

    getSales: function () { return request('/sales'); },

    /*
     * ── Recent images ─────────────────────────────────────────────────────
     * Every image the seller has put on a shirt, kept against their customer
     * id. All three return the whole list, because the builder renders
     * straight from it and the order is the point.
     */
    getRecentImages: function () { return request('/recent-images'); },

    // Also the "move to front" call: the server treats a repeat as a touch
    // rather than a second row.
    rememberRecentImage: function (image) {
      return request('/recent-images', { method: 'POST', body: image });
    },

    forgetRecentImage: function (url) {
      return request('/recent-images', { method: 'DELETE', body: { url: url } });
    },

    /*
     * ── Categories ────────────────────────────────────────────────────────
     * The seller's own groupings of their designs. Every one of these comes
     * back with the FULL category list rather than just what changed: the
     * strip shows counts and order, both of which a single change can move,
     * and re-rendering from the server's answer is how the screen stays
     * honest without the browser re-deriving it.
     */
    createCategory: function (name) {
      return request('/categories', { method: 'POST', body: { name: name } });
    },

    renameCategory: function (uid, name) {
      return request('/categories', { method: 'PUT', body: { uid: uid, name: name } });
    },

    deleteCategory: function (uid) {
      return request('/categories', { method: 'DELETE', body: { uid: uid } });
    },

    reorderCategories: function (uids) {
      return request('/categories/order', { method: 'PUT', body: { uids: uids } });
    },

    /*
     * The order of the store itself -- what All items shows, and therefore
     * what a shopper sees on the shop front and in the collection.
     *
     * Sent whole rather than as "this one moved to position four", so the
     * server never has to reconcile a move against an order it might not
     * share with the browser.
     */
    reorderListings: function (uids) {
      return request('/listings/order', { method: 'PUT', body: { uids: uids } });
    },

    // What is in one category, in order -- the drag-to-arrange write.
    setCategoryMembers: function (uid, listingUids) {
      return request('/categories/members', { method: 'PUT', body: { uid: uid, listingUids: listingUids } });
    },

    // Which categories one design is in -- the write the card's chips make.
    setListingCategories: function (uid, categoryUids) {
      return request('/listing/categories', { method: 'PUT', body: { uid: uid, categoryUids: categoryUids } });
    },

    // Which category the store opens on. An empty uid means All items.
    setDefaultCategory: function (uid) {
      return request('/categories/default', { method: 'PUT', body: { uid: uid || '' } });
    },

    // The store page's own view, by store handle. Public: a shopper browsing
    // a seller's shop has nobody signed in.
    getStorefront: function (handle) {
      return request('/storefront?handle=' + encodeURIComponent(handle));
    },

    // Admin only; both reject for anyone else.
    listStores: function () { return request('/admin/stores'); },
    payables: function () { return request('/admin/payables'); }
  };

  // ── Helpers the theme surfaces share ──────────────────────────────────────

  /*
   * Whole dollars, no cents.
   *
   * Every customer-facing price is rounded up to a whole dollar server-side, so
   * showing ".00" would be noise. Anything that somehow is not whole keeps its
   * cents rather than being rounded again here -- this formats, it does not
   * decide prices.
   */
  CCSApi.money = function (value) {
    var amount = Number(value);
    if (!isFinite(amount)) return '';
    return amount % 1 === 0
      ? '$' + amount.toFixed(0)
      : '$' + amount.toFixed(2);
  };

  CCSApi.priceRange = function (min, max) {
    if (min == null && max == null) return '';
    if (min == null || max == null || Number(min) === Number(max)) {
      return CCSApi.money(min == null ? max : min);
    }
    return CCSApi.money(min) + '–' + CCSApi.money(max);
  };

  /*
   * Store name -> URL handle, mirroring lib/handles.js on the server.
   *
   * Duplicated deliberately: this is for live feedback as the seller types, and
   * a round trip per keystroke would be worse. The server still validates --
   * this is a preview, never the decision.
   */
  CCSApi.slugify = function (input) {
    return String(input == null ? '' : input)
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/g, '');
  };

  window.CCSApi = CCSApi;
})(window);
